
  import { defineComponent, markRaw } from 'vue';
  import { mapActions, mapGetters } from 'vuex';

  import { TFilterRangeNumber, TFilterSelect, TFilterText } from '@/common/components/redesigned/Filter/types/filter';
  import PageTitle from '@/common/components/redesigned/PageTitle.vue';
  import StatusBadge from '@/common/components/redesigned/StatusBadge.vue';
  import TableWithOuterFilter from '@/common/components/redesigned/Table/TableWithOuterFilter.vue';
  import { TTableWithOuterFilterConfig } from '@/common/components/redesigned/Table/types/tableTypes';
  import text from '@/common/mixins/text';
  import Utils from '@/common/utils';
  import { downloadPDF } from '@/common/utils/fileExport';
  import { resetCurrentRouteQuery } from '@/common/utils/router';

  import DateActualDataSubtitle from '../components/common/DateActualDataSubtitle.vue';
  import CountTable from '../components/CountTable.vue';
  import DashboardsYearFilterBlock from '../components/dashboards/DashboardsYearFilterBlock.vue';
  import VesExportButtons from '../components/VesExportButtons.vue';
  import { DEFAULT_DASHBOARDS_FILTER } from '../constants/dashboards';
  import tablePagePermission from '../mixins/tablePagePermission';
  import { BASE_VES_ACTIONS_NAME, BASE_VES_GETTERS_NAME } from '../store/base/name';
  import {
    PROJECT_WITH_REGIONS_ACTIONS_NAME,
    PROJECT_WITH_REGIONS_GETTERS_NAME,
    PROJECT_WITH_REGIONS_MUTATIONS_NAME,
  } from '../store/projectWithRegions/name';
  import { TStorePathsCountTable } from '../types/common';
  import type { ProjectWithRegionsFilterData, ProjectWithRegionsTableData } from '../types/projectWithRegions';
  import { getFilteredRegionsForUserRole } from '../utils/getFilteredRegionsForUserRole';
  import { getStatusBadgeProps } from '../utils/statuses';

  import { DIRECTIVES_GETTERS_NAME } from '@/store/directives';
  import { DIRECTIVES_ACTIONS_NAME } from '@/store/directives';
  import Links from '@/utils/links';

  const FILENAME = 'Проекты_с_иностранным_участием_в_субъектах_РФ';

  export default defineComponent({
    name: 'ProjectsWithRegions',
    components: {
      PageTitle,
      VesExportButtons,
      CountTable,
      TableWithOuterFilter,
      // eslint-disable-next-line vue/no-unused-components
      StatusBadge,
      DashboardsYearFilterBlock,
      DateActualDataSubtitle,
    },
    mixins: [tablePagePermission, text],
    created() {
      this.getDirectives();
      // ? Для администратора ВЭС не работает по ТЗ, поэтому пока закомментировал
      // this.checkTablePagePermission();
    },
    data(): {
      exportInProgress: boolean;
      countTableConfig: TStorePathsCountTable;
      filter: Partial<ProjectWithRegionsFilterData>;
    } {
      return {
        exportInProgress: false,
        countTableConfig: {
          action: PROJECT_WITH_REGIONS_ACTIONS_NAME.getProjectsWithForeignParticipationDatesCount,
          getter: PROJECT_WITH_REGIONS_GETTERS_NAME.getProjectsWithForeignParticipationDatesCount,
          loadingGetter: PROJECT_WITH_REGIONS_GETTERS_NAME.getIsLoadingCount,
        },
        filter: {
          ...DEFAULT_DASHBOARDS_FILTER,
          regionRfId: this.$route?.query?.regionRfId ? Number(this.$route.query.regionRfId) : undefined,
          partnerCountryId: this.$route?.query?.partnerCountryId
            ? Number(this.$route.query.partnerCountryId)
            : undefined,
          year: this.$route?.query?.year ? String(this.$route.query.year) : DEFAULT_DASHBOARDS_FILTER.year,
        },
      };
    },
    computed: {
      ...mapGetters({
        partnerCountryList: DIRECTIVES_GETTERS_NAME.getPartnerCountrySearchIdList,
        tnvedIndustryList: DIRECTIVES_GETTERS_NAME.getNSIIndustryList,
        statusList: BASE_VES_GETTERS_NAME.getStatusList,
        regionsListWithId: DIRECTIVES_GETTERS_NAME.getRegionsListWithId,
      }),
      getFilteredRegionsList() {
        return getFilteredRegionsForUserRole(this.regionsListWithId);
      },
      getTableConfig(): TTableWithOuterFilterConfig<ProjectWithRegionsTableData> {
        return {
          columns: [
            {
              field: 'regionRf',
              title: 'Субъект РФ',
              width: 138,
            },
            {
              field: 'partnerCountry',
              title: 'Страна-партнер',
              width: 138,
            },
            {
              field: 'tnvedIndustry',
              title: 'Отрасль',
              width: 174,
            },
            {
              field: 'projectName',
              title: 'Наименование проекта',
              width: 309,
            },
            {
              field: 'projectDescription',
              title: 'Описание проекта',
              width: 332,
            },
            {
              field: 'investmentAmount',
              title: 'Сумма инвестиций, млн ₽',
              width: 115,
              formatter: (value) => {
                return typeof value.investmentAmount === 'number' && !Number.isNaN(value.investmentAmount)
                  ? this.formatNumber(value.investmentAmount, 1, ' ', ',')
                  : 'Нет данных';
              },
            },
            {
              field: 'status',
              title: 'Статус проекта',
              width: 127,
              renderComponent: {
                component: markRaw(StatusBadge),
                props: getStatusBadgeProps,
              },
            },
          ],
          storePaths: {
            action: PROJECT_WITH_REGIONS_ACTIONS_NAME.searchProjects,
            mutation: PROJECT_WITH_REGIONS_MUTATIONS_NAME.setListOfProjects,
            getter: PROJECT_WITH_REGIONS_GETTERS_NAME.getListOfProjects,
          },
          isShowNumberColumn: true,
          params: {
            ...this.filter,
          },
        };
      },
      getFiltersConfig(): [
        TFilterSelect<{ id: number; name: string }>,
        TFilterSelect<{ id: number; name: string }>,
        TFilterSelect<{ value: string; label: string }>,
        TFilterText,
        TFilterText,
        TFilterRangeNumber,
        TFilterSelect<{ id: number; name: string }>,
      ] {
        return [
          {
            type: 'select',
            label: 'Субъект РФ',
            options: this.getFilteredRegionsList,
            valueProp: 'id',
            labelProp: 'name',
            vModel: 'regionRfId',
          },
          {
            type: 'select',
            label: 'Страна-партнер',
            options: this.partnerCountryList,
            valueProp: 'id',
            labelProp: 'name',
            vModel: 'partnerCountryId',
          },
          {
            type: 'select',
            label: 'Отрасль',
            options: this.tnvedIndustryList,
            valueProp: 'value',
            labelProp: 'label',
            vModel: 'tnvedIndustryId',
          },
          {
            type: 'text',
            label: 'Наименование проекта',
            placeholder: 'Введите наименование',
            vModel: 'projectName',
          },
          {
            type: 'text',
            label: 'Описание проекта',
            placeholder: 'Введите описание',
            vModel: 'projectDescription',
          },
          {
            type: 'rangeNumber',
            label: 'Сумма инвестиций, млн руб',
            placeholderFrom: 'от',
            placeholderTo: 'до',
            vModel: 'investmentAmount',
          },
          {
            type: 'select',
            label: 'Статус проекта',
            options: this.statusList,
            valueProp: 'id',
            labelProp: 'name',
            vModel: 'statusId',
          },
        ];
      },
    },
    mounted() {
      this.getStatusList();
      resetCurrentRouteQuery(this);
    },
    methods: {
      ...mapActions({
        getPartnerCountrySearchId: DIRECTIVES_ACTIONS_NAME.getPartnerCountrySearchId,
        getNSIIndustry: DIRECTIVES_ACTIONS_NAME.getNSIIndustry,
        getStatusList: BASE_VES_ACTIONS_NAME.getStatusList,
        getRegions: DIRECTIVES_ACTIONS_NAME.getRegions,
      }),

      updateYearFilter(data: Partial<ProjectWithRegionsFilterData>) {
        this.filter = { ...this.filter, year: data.year };
      },

      updateTableFilters(data: Partial<ProjectWithRegionsFilterData>) {
        this.filter = { ...data, year: this.filter.year };
      },

      async exportExcel() {
        this.exportInProgress = true;
        try {
          await Utils.downloadFile(
            Links.ves.projectWithRegions.exportProjectsExcel,
            `${FILENAME}.xlsx`,
            this.filter,
            true,
          );
        } finally {
          this.exportInProgress = false;
        }
      },

      async downloadPDF() {
        this.exportInProgress = true;
        try {
          await downloadPDF({
            clonedElementClassName: 'project-with-regions__export-pdf',
            pagebreakMode: 'table-friendly',
            fileName: FILENAME,
          });
        } finally {
          this.exportInProgress = false;
        }
      },

      async getDirectives() {
        this.getRegions();
        this.getPartnerCountrySearchId();
        this.getNSIIndustry();
      },
    },
  });
